import { useState, useEffect } from "react";
import axios from "axios";
import { API, BACKEND_URL, useAuth } from "../../App";
import { descargarArchivo } from "../../utils/download";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { 
  Settings, 
  CreditCard, 
  Users,
  Shield,
  Trash2,
  Plus,
  Mail,
  Download,
  Upload,
  Database,
  Building2,
  Save
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";

const Configuracion = () => {
  const { user } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [inviteDialog, setInviteDialog] = useState(false);
  const [inviteData, setInviteData] = useState({ email: "", nombre: "", role: "user" });
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [empresa, setEmpresa] = useState(null);
  const [empresaLoading, setEmpresaLoading] = useState(true);
  const [savingEmpresa, setSavingEmpresa] = useState(false);

  // Iter130 · datosPago YA NO se lee de un hardcode. Se deriva del propio tenant.
  const datosPago = {
    banco: empresa?.banco || "",
    titular: empresa?.beneficiario || empresa?.nombre_comercial || "",
    clabe: empresa?.clabe || "",
    cuenta: empresa?.cuenta || "",
    whatsapp: empresa?.whatsapp || empresa?.telefono || "",
    instrucciones: empresa?.instrucciones_pago || "",
  };

  useEffect(() => {
    const fetchUsuarios = async () => {
      if (user?.role === "superadmin") {
        try {
          const response = await axios.get(`${API}/admin/usuarios`);
          setUsuarios(response.data);
        } catch {
          toast.error("Error al cargar usuarios");
        }
      }
      setLoading(false);
    };
    fetchUsuarios();

    // Iter130 · Fuente canónica: /api/tenant/mi-empresa (db.empresas)
    // Con fallback a /api/configuracion-empresa por compat con datos históricos
    axios.get(`${API}/tenant/mi-empresa`).then(async (res) => {
      const emp = res.data?.empresa || {};
      // Merge con configuracion-empresa legacy solo para llenar campos faltantes
      try {
        const legacyRes = await axios.get(`${API}/configuracion-empresa`);
        const legacy = legacyRes.data || {};
        const merged = { ...legacy, ...emp }; // canonical wins
        setEmpresa(merged);
      } catch {
        setEmpresa(emp);
      }
      setEmpresaLoading(false);
    }).catch(() => {
      // Fallback total a la colección legacy si el nuevo endpoint falla
      axios.get(`${API}/configuracion-empresa`).then(res => {
        setEmpresa(res.data);
      }).finally(() => setEmpresaLoading(false));
    });
  }, [user]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/usuarios/${userId}/rol`, { role: newRole });
      setUsuarios(prev => prev.map(u => u.user_id === userId ? { ...u, role: newRole } : u));
      toast.success("Rol actualizado");
    } catch (error) {
      toast.error("Error al actualizar el rol");
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUserId) return;
    try {
      await axios.delete(`${API}/admin/usuarios/${deleteUserId}`);
      setUsuarios(prev => prev.filter(u => u.user_id !== deleteUserId));
      toast.success("Usuario eliminado");
    } catch (error) {
      toast.error("Error al eliminar el usuario");
    } finally {
      setDeleteUserId(null);
    }
  };

  const handleInviteUser = async () => {
    if (!inviteData.email) {
      toast.error("El email es requerido");
      return;
    }
    try {
      await axios.post(`${API}/admin/usuarios/invitar`, inviteData);
      toast.success(`Usuario ${inviteData.email} registrado. Podrá acceder con Google OAuth.`);
      setInviteDialog(false);
      setInviteData({ email: "", nombre: "", role: "user" });
      // Refresh users
      const response = await axios.get(`${API}/admin/usuarios`);
      setUsuarios(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al invitar usuario");
    }
  };

  const handleDescargarJSON = async (modulo) => {
    toast.info(`Exportando ${modulo}...`);
    try {
      const response = await axios.get(`${API}/admin/exportar/${modulo}`);
      descargarArchivo(response.data.url, response.data.filename);
      toast.success(`JSON de ${modulo} descargado`);
    } catch (error) {
      toast.error(`Error al exportar ${modulo}`);
    }
  };

  const handleExportar = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/admin/exportar`);
      descargarArchivo(response.data.url, response.data.filename);
      toast.success("Backup JSON descargado");
    } catch (error) {
      toast.error("Error al exportar datos");
    } finally {
      setExporting(false);
    }
  };

  const handleDescargarPDF = async (tipo) => {
    toast.info(`Generando PDF de ${tipo}...`);
    try {
      const response = await axios.get(`${API}/admin/exportar/pdf/${tipo}`);
      descargarArchivo(response.data.url, response.data.filename);
      toast.success(`PDF de ${tipo} descargado`);
    } catch (error) {
      toast.error(`Error al descargar PDF de ${tipo}`);
    }
  };

  const handleImportar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const text = await file.text();
      const jsonData = JSON.parse(text);
      const { data } = await axios.post(`${API}/admin/importar`, jsonData);
      setImportResult(data.imported);
      toast.success(`Importado: ${data.imported.clientes} clientes, ${data.imported.contratos} contratos, ${data.imported.eventos} eventos`);
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error("El archivo no es JSON válido");
      } else {
        toast.error(error.response?.data?.detail || "Error al importar datos");
      }
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  const handleSaveEmpresa = async () => {
    setSavingEmpresa(true);
    try {
      // Iter130 · Fuente canónica db.empresas + compat legacy
      await axios.put(`${API}/tenant/mi-empresa`, empresa);
      try { await axios.put(`${API}/configuracion-empresa`, empresa); } catch (_e) { /* legacy opcional */ }
      toast.success("Configuración de empresa guardada");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Error al guardar configuración");
    } finally {
      setSavingEmpresa(false);
    }
  };

  const updateEmpresa = (field, value) => {
    setEmpresa(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-display text-xl sm:text-2xl lg:text-3xl text-stone-900">Configuracion</h1>
        <p className="text-xs sm:text-sm text-stone-600 mt-1">Ajustes del sistema</p>
      </div>

      <Tabs defaultValue="empresa" className="space-y-6">
        <TabsList className="bg-stone-100 w-full flex flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="empresa" data-testid="tab-config-empresa" className="flex-1 min-w-0 text-xs sm:text-sm">
            <Building2 className="w-4 h-4 mr-1 sm:mr-2 shrink-0" />
            <span className="truncate">Empresa</span>
          </TabsTrigger>
          <TabsTrigger value="pago" data-testid="tab-config-pago" className="flex-1 min-w-0 text-xs sm:text-sm">
            <CreditCard className="w-4 h-4 mr-1 sm:mr-2 shrink-0" />
            <span className="truncate">Pago</span>
          </TabsTrigger>
          {user?.role === "superadmin" && (
            <TabsTrigger value="usuarios" data-testid="tab-config-usuarios" className="flex-1 min-w-0 text-xs sm:text-sm">
              <Users className="w-4 h-4 mr-1 sm:mr-2 shrink-0" />
              <span className="truncate">Usuarios</span>
            </TabsTrigger>
          )}
          <TabsTrigger value="datos" data-testid="tab-config-datos" className="flex-1 min-w-0 text-xs sm:text-sm">
            <Database className="w-4 h-4 mr-1 sm:mr-2 shrink-0" />
            <span className="truncate">Datos</span>
          </TabsTrigger>
          <TabsTrigger value="modulos" data-testid="tab-config-modulos" className="flex-1 min-w-0 text-xs sm:text-sm">
            <Settings className="w-4 h-4 mr-1 sm:mr-2 shrink-0" />
            <span className="truncate">Módulos</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="modulos">
          <ModulosVisiblesPanel />
        </TabsContent>

        {/* Empresa Tab */}
        <TabsContent value="empresa">
          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle className="font-display text-xl flex items-center gap-2">
                <Building2 className="w-5 h-5 text-emerald-900" />
                Datos de la Empresa
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-stone-600 text-sm">
                Estos datos se aplican automaticamente a todos los contratos PDF generados.
              </p>

              {empresaLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="w-6 h-6 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : empresa ? (
                <>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nombre comercial</Label>
                      <Input value={empresa.nombre_comercial || ""} onChange={e => updateEmpresa("nombre_comercial", e.target.value)} className="border-stone-300" data-testid="input-empresa-nombre" />
                    </div>
                    <div className="space-y-2">
                      <Label>Razon social</Label>
                      <Input value={empresa.razon_social || ""} onChange={e => updateEmpresa("razon_social", e.target.value)} className="border-stone-300" data-testid="input-empresa-razon" />
                    </div>
                    <div className="space-y-2">
                      <Label>RFC</Label>
                      <Input value={empresa.rfc || ""} onChange={e => updateEmpresa("rfc", e.target.value)} className="border-stone-300" data-testid="input-empresa-rfc" />
                    </div>
                    <div className="space-y-2">
                      <Label>Telefono</Label>
                      <Input value={empresa.telefono || ""} onChange={e => updateEmpresa("telefono", e.target.value)} className="border-stone-300" data-testid="input-empresa-telefono" />
                    </div>
                    <div className="space-y-2">
                      <Label>Locacion</Label>
                      <Input value={empresa.locacion || ""} onChange={e => updateEmpresa("locacion", e.target.value)} className="border-stone-300" data-testid="input-empresa-locacion" />
                    </div>
                    <div className="space-y-2">
                      <Label>Ubicacion</Label>
                      <Input value={empresa.ubicacion || ""} onChange={e => updateEmpresa("ubicacion", e.target.value)} className="border-stone-300" data-testid="input-empresa-ubicacion" />
                    </div>
                  </div>

                  <div className="border border-stone-200 rounded-sm p-4 space-y-4">
                    <h3 className="font-medium text-stone-900 text-sm">Responsables</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Gerente</Label>
                        <Input value={empresa.gerente || ""} onChange={e => updateEmpresa("gerente", e.target.value)} className="border-stone-300" data-testid="input-empresa-gerente" />
                      </div>
                      <div className="space-y-2">
                        <Label>Chef</Label>
                        <Input value={empresa.chef || ""} onChange={e => updateEmpresa("chef", e.target.value)} className="border-stone-300" data-testid="input-empresa-chef" />
                      </div>
                    </div>
                  </div>

                  <div className="border border-stone-200 rounded-sm p-4 space-y-4">
                    <h3 className="font-medium text-stone-900 text-sm">Contacto Operativo (opcional en contrato)</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Nombre</Label>
                        <Input value={empresa.contacto_operativo_nombre || ""} onChange={e => updateEmpresa("contacto_operativo_nombre", e.target.value)} className="border-stone-300" data-testid="input-empresa-contacto-nombre" />
                      </div>
                      <div className="space-y-2">
                        <Label>Rol</Label>
                        <Input value={empresa.contacto_operativo_rol || ""} onChange={e => updateEmpresa("contacto_operativo_rol", e.target.value)} className="border-stone-300" data-testid="input-empresa-contacto-rol" />
                      </div>
                    </div>
                  </div>

                  <div className="border border-stone-200 rounded-sm p-4 space-y-4">
                    <h3 className="font-medium text-stone-900 text-sm">Dirección</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2"><Label>Calle</Label><Input value={empresa.calle || ""} onChange={e => updateEmpresa("calle", e.target.value)} className="border-stone-300" data-testid="input-empresa-calle" /></div>
                      <div className="space-y-2"><Label>Colonia</Label><Input value={empresa.colonia || ""} onChange={e => updateEmpresa("colonia", e.target.value)} className="border-stone-300" data-testid="input-empresa-colonia" /></div>
                      <div className="space-y-2"><Label>Ciudad</Label><Input value={empresa.ciudad || ""} onChange={e => updateEmpresa("ciudad", e.target.value)} className="border-stone-300" data-testid="input-empresa-ciudad" /></div>
                      <div className="space-y-2"><Label>Estado</Label><Input value={empresa.estado || ""} onChange={e => updateEmpresa("estado", e.target.value)} className="border-stone-300" data-testid="input-empresa-estado" /></div>
                      <div className="space-y-2"><Label>CP</Label><Input value={empresa.cp || ""} onChange={e => updateEmpresa("cp", e.target.value)} className="border-stone-300" data-testid="input-empresa-cp" /></div>
                      <div className="space-y-2"><Label>País</Label><Input value={empresa.pais || ""} onChange={e => updateEmpresa("pais", e.target.value)} className="border-stone-300" data-testid="input-empresa-pais" /></div>
                      <div className="space-y-2 md:col-span-2"><Label>Enlace Google Maps</Label><Input value={empresa.mapa_url || ""} onChange={e => updateEmpresa("mapa_url", e.target.value)} className="border-stone-300" data-testid="input-empresa-mapa" /></div>
                    </div>
                  </div>

                  <div className="border border-stone-200 rounded-sm p-4 space-y-4">
                    <h3 className="font-medium text-stone-900 text-sm">Datos Fiscales</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2"><Label>Razón social fiscal</Label><Input value={empresa.razon_social_fiscal || ""} onChange={e => updateEmpresa("razon_social_fiscal", e.target.value)} className="border-stone-300" data-testid="input-empresa-razon-fiscal" /></div>
                      <div className="space-y-2"><Label>RFC fiscal</Label><Input value={empresa.rfc_fiscal || ""} onChange={e => updateEmpresa("rfc_fiscal", e.target.value)} className="border-stone-300" data-testid="input-empresa-rfc-fiscal" /></div>
                      <div className="space-y-2 md:col-span-2"><Label>Domicilio fiscal</Label><Input value={empresa.domicilio_fiscal || ""} onChange={e => updateEmpresa("domicilio_fiscal", e.target.value)} className="border-stone-300" data-testid="input-empresa-domicilio-fiscal" /></div>
                      <div className="space-y-2"><Label>Correo de facturación</Label><Input value={empresa.correo_facturacion || ""} onChange={e => updateEmpresa("correo_facturacion", e.target.value)} className="border-stone-300" data-testid="input-empresa-correo-facturacion" /></div>
                    </div>
                  </div>

                  <div className="border border-stone-200 rounded-sm p-4 space-y-4">
                    <h3 className="font-medium text-stone-900 text-sm">Datos de Pago</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2"><Label>Banco</Label><Input value={empresa.banco || ""} onChange={e => updateEmpresa("banco", e.target.value)} className="border-stone-300" data-testid="input-empresa-banco" /></div>
                      <div className="space-y-2"><Label>Beneficiario</Label><Input value={empresa.beneficiario || ""} onChange={e => updateEmpresa("beneficiario", e.target.value)} className="border-stone-300" data-testid="input-empresa-beneficiario" /></div>
                      <div className="space-y-2"><Label>Cuenta</Label><Input value={empresa.cuenta || ""} onChange={e => updateEmpresa("cuenta", e.target.value)} className="border-stone-300" data-testid="input-empresa-cuenta" /></div>
                      <div className="space-y-2"><Label>CLABE</Label><Input value={empresa.clabe || ""} onChange={e => updateEmpresa("clabe", e.target.value)} className="border-stone-300" data-testid="input-empresa-clabe" /></div>
                      <div className="space-y-2 md:col-span-2"><Label>Instrucciones de pago</Label><Textarea value={empresa.instrucciones_pago || ""} onChange={e => updateEmpresa("instrucciones_pago", e.target.value)} rows={3} className="border-stone-300 text-sm" data-testid="input-empresa-instrucciones-pago" /></div>
                    </div>
                  </div>

                  <div className="border border-stone-200 rounded-sm p-4 space-y-4">
                    <h3 className="font-medium text-stone-900 text-sm">Terminos y Condiciones del Contrato</h3>
                    <p className="text-xs text-stone-500">Se incluiran automaticamente en todos los contratos PDF. No editables por el cliente.</p>
                    <Textarea
                      value={empresa.terminos_condiciones || ""}
                      onChange={e => updateEmpresa("terminos_condiciones", e.target.value)}
                      rows={16}
                      className="border-stone-300 text-sm font-mono"
                      data-testid="textarea-terminos"
                    />
                  </div>

                  <Button onClick={handleSaveEmpresa} disabled={savingEmpresa} className="btn-primary" data-testid="btn-guardar-empresa">
                    <Save className="w-4 h-4 mr-2" />
                    {savingEmpresa ? "Guardando..." : "Guardar Configuracion"}
                  </Button>
                </>
              ) : (
                <p className="text-stone-500">Error al cargar configuracion</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Datos de Pago Tab */}
        <TabsContent value="pago">
          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle className="font-display text-xl flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-emerald-900" />
                Datos de Pago
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-stone-600">
                Estos son los datos que se muestran a los clientes para realizar sus pagos.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Banco</Label>
                  <Input 
                    value={datosPago.banco}
                    readOnly
                    className="border-stone-300 bg-stone-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Titular</Label>
                  <Input 
                    value={datosPago.titular}
                    readOnly
                    className="border-stone-300 bg-stone-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>CLABE</Label>
                  <Input 
                    value={datosPago.clabe}
                    readOnly
                    className="border-stone-300 bg-stone-50 font-mono"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>WhatsApp de contacto</Label>
                  <Input 
                    value={datosPago.whatsapp}
                    readOnly
                    className="border-stone-300 bg-stone-50"
                  />
                </div>
              </div>

              <div className="p-4 bg-amber-50 border border-amber-200 rounded-sm">
                <p className="text-sm text-amber-800">
                  <strong>Nota:</strong> Para modificar estos datos, contacta al administrador del sistema.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Usuarios Tab (Solo SuperAdmin) */}
        {user?.role === "superadmin" && (
          <TabsContent value="usuarios">
            <Card className="border-stone-200">
              <CardHeader>
                <CardTitle className="font-display text-xl flex items-center gap-2">
                  <Shield className="w-5 h-5 text-emerald-900" />
                  Gestión de Usuarios
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between mb-6">
                  <p className="text-stone-600">
                    Administra los usuarios del sistema y sus permisos.
                  </p>
                  <Button onClick={() => setInviteDialog(true)} className="btn-primary" data-testid="btn-invitar-usuario">
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Usuario
                  </Button>
                </div>

                {loading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="w-6 h-6 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : usuarios.length > 0 ? (
                  <div className="space-y-4">
                    {usuarios.map(u => (
                      <div 
                        key={u.user_id} 
                        className="p-3 sm:p-4 bg-stone-50 rounded-sm space-y-3"
                        data-testid={`user-row-${u.user_id}`}
                      >
                        <div className="flex items-center gap-3">
                          {u.picture ? (
                            <img 
                              src={u.picture} 
                              alt={u.name}
                              className="w-10 h-10 rounded-full shrink-0"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center shrink-0">
                              <span className="text-emerald-900 font-medium">
                                {u.name?.charAt(0) || 'U'}
                              </span>
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="font-medium text-stone-900 text-sm sm:text-base truncate">{u.name}</p>
                            <p className="text-xs sm:text-sm text-stone-500 truncate">{u.email}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 pl-0 sm:pl-13">
                          <Select 
                            value={u.role} 
                            onValueChange={(v) => handleRoleChange(u.user_id, v)}
                            disabled={u.user_id === user.user_id}
                          >
                            <SelectTrigger className="flex-1 sm:w-36 sm:flex-none border-stone-300 text-xs sm:text-sm" data-testid={`select-role-${u.user_id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="user">Usuario</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="superadmin">Super Admin</SelectItem>
                            </SelectContent>
                          </Select>
                          
                          {u.user_id !== user.user_id && (
                            <Button 
                              variant="outline"
                              size="sm"
                              onClick={() => setDeleteUserId(u.user_id)}
                              className="border-red-300 text-red-600 hover:bg-red-50"
                              data-testid={`btn-delete-user-${u.user_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-stone-500">
                    <Users className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                    <p>No hay usuarios</p>
                  </div>
                )}

                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-sm">
                  <p className="text-sm text-blue-800">
                    <strong>Roles:</strong>
                  </p>
                  <ul className="text-sm text-blue-700 mt-2 space-y-1">
                    <li>• <strong>Admin:</strong> Acceso a todas las funciones excepto gestión de usuarios</li>
                    <li>• <strong>Super Admin:</strong> Acceso completo incluyendo gestión de usuarios</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Datos Tab */}
        <TabsContent value="datos">
          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle className="font-display text-xl flex items-center gap-2">
                <Database className="w-5 h-5 text-emerald-900" />
                Exportar / Importar Datos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-stone-600">
                Exporta tus datos para respaldo o para migrarlos a otro ambiente (ej: del preview a producción).
              </p>

              {/* PDF Reports */}
              <div className="border border-stone-200 rounded-sm p-5 space-y-3">
                <h3 className="font-medium text-stone-900 flex items-center gap-2">
                  <Download className="w-4 h-4 text-red-600" />
                  Descargar Reportes PDF
                </h3>
                <p className="text-sm text-stone-500">
                  Descarga un PDF con la lista completa de cada módulo.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {[
                    { id: "clientes", label: "Clientes" },
                    { id: "cotizaciones", label: "Cotizaciones" },
                    { id: "contratos", label: "Contratos" },
                    { id: "pagos", label: "Pagos" },
                    { id: "eventos", label: "Eventos" },
                    { id: "personal", label: "Personal" },
                    { id: "proveedores", label: "Proveedores" },
                  ].map(item => (
                    <Button
                      key={item.id}
                      variant="outline"
                      size="sm"
                      className="border-stone-300 text-xs justify-start"
                      onClick={() => handleDescargarPDF(item.id)}
                      data-testid={`btn-pdf-${item.id}`}
                    >
                      <Download className="w-3 h-3 mr-1.5 text-red-500" />
                      {item.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* JSON Individual Downloads */}
              <div className="border border-stone-200 rounded-sm p-5 space-y-3">
                <h3 className="font-medium text-stone-900 flex items-center gap-2">
                  <Download className="w-4 h-4 text-blue-600" />
                  Descargar JSON por Modulo
                </h3>
                <p className="text-sm text-stone-500">
                  Descarga un archivo JSON individual de cada modulo con datos reales.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {[
                    { id: "catalogo", label: "Catalogo" },
                    { id: "servicios_rapidos", label: "Servicios Rapidos" },
                    { id: "clientes", label: "Clientes" },
                    { id: "cotizaciones", label: "Cotizaciones" },
                    { id: "contratos", label: "Contratos" },
                    { id: "eventos", label: "Eventos" },
                    { id: "pagos", label: "Pagos" },
                    { id: "personal", label: "Personal" },
                    { id: "proveedores", label: "Proveedores" },
                    { id: "compras", label: "Compras" },
                    { id: "menus", label: "Menus" },
                  ].map(item => (
                    <Button
                      key={item.id}
                      variant="outline"
                      size="sm"
                      className="border-stone-300 text-xs justify-start"
                      onClick={() => handleDescargarJSON(item.id)}
                      data-testid={`btn-json-${item.id}`}
                    >
                      <Download className="w-3 h-3 mr-1.5 text-blue-500" />
                      {item.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Exportar */}
              <div className="border border-stone-200 rounded-sm p-5 space-y-3">
                <h3 className="font-medium text-stone-900 flex items-center gap-2">
                  <Download className="w-4 h-4 text-emerald-700" />
                  Exportar Datos
                </h3>
                <p className="text-sm text-stone-500">
                  Descarga un archivo JSON con todos tus clientes, contratos y eventos. Puedes usarlo para importar en otro ambiente.
                </p>
                <Button 
                  onClick={handleExportar} 
                  disabled={exporting} 
                  className="btn-primary"
                  data-testid="btn-exportar-datos"
                >
                  {exporting ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Descargar Backup (.json)
                </Button>
              </div>

              {/* Importar */}
              <div className="border border-stone-200 rounded-sm p-5 space-y-3">
                <h3 className="font-medium text-stone-900 flex items-center gap-2">
                  <Upload className="w-4 h-4 text-blue-700" />
                  Importar Datos
                </h3>
                <p className="text-sm text-stone-500">
                  Sube un archivo JSON exportado desde CelebraMid. Los datos existentes NO se duplicarán.
                </p>
                <div className="flex items-center gap-3">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".json"
                      onChange={handleImportar}
                      className="hidden"
                      data-testid="input-importar-archivo"
                    />
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors cursor-pointer">
                      {importing ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                      Subir archivo .json
                    </div>
                  </label>
                </div>
                {importResult && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-sm">
                    <p className="font-medium text-emerald-800 mb-1">Importación completada:</p>
                    <ul className="text-emerald-700 space-y-0.5">
                      <li>Clientes importados: <strong>{importResult.clientes}</strong></li>
                      <li>Contratos importados: <strong>{importResult.contratos}</strong></li>
                      <li>Eventos importados: <strong>{importResult.eventos}</strong></li>
                      {importResult.skipped > 0 && (
                        <li className="text-amber-700">Registros omitidos (ya existían): <strong>{importResult.skipped}</strong></li>
                      )}
                    </ul>
                  </div>
                )}
              </div>

              <div className="p-4 bg-amber-50 border border-amber-200 rounded-sm">
                <p className="text-sm text-amber-800">
                  <strong>Nota:</strong> La importación no duplica datos. Si un cliente, contrato o evento ya existe (mismo ID), se omite automáticamente.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete User Confirmation */}
      <AlertDialog open={!!deleteUserId} onOpenChange={() => setDeleteUserId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar usuario?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. El usuario perderá acceso al sistema.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteUser}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Invite User Dialog */}
      <Dialog open={inviteDialog} onOpenChange={setInviteDialog}>
        <DialogContent className="max-w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-lg sm:text-xl">Agregar Usuario</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Email (Google) *</Label>
              <Input
                type="email"
                placeholder="usuario@gmail.com"
                value={inviteData.email}
                onChange={(e) => setInviteData(prev => ({ ...prev, email: e.target.value }))}
                className="border-stone-300"
                data-testid="input-invite-email"
              />
              <p className="text-xs text-stone-500">El usuario accederá con esta cuenta de Google</p>
            </div>
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input
                placeholder="Nombre del usuario"
                value={inviteData.nombre}
                onChange={(e) => setInviteData(prev => ({ ...prev, nombre: e.target.value }))}
                className="border-stone-300"
                data-testid="input-invite-nombre"
              />
            </div>
            <div className="space-y-2">
              <Label>Rol</Label>
              <Select value={inviteData.role} onValueChange={(v) => setInviteData(prev => ({ ...prev, role: v }))}>
                <SelectTrigger className="border-stone-300" data-testid="select-invite-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Usuario</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="superadmin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setInviteDialog(false)} className="border-stone-300 w-full sm:w-auto">Cancelar</Button>
            <Button onClick={handleInviteUser} className="btn-primary w-full sm:w-auto" data-testid="btn-confirmar-invitacion">
              <Mail className="w-4 h-4 mr-2" />
              Registrar Usuario
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Configuracion;

// Iter149 · Panel "Módulos de mi espacio de trabajo".
// Solo muestra switches para módulos permitidos por el plan (user.plan_modulos).
// Guarda en empresas.modulos_visibles vía PUT /api/mi-empresa/modulos-visibles.
// Ocultar solo esconde de la UI: nunca borra datos ni cambia permisos.
const MODULOS_LABELS = {
  dashboard: "Dashboard",
  clientes: "Clientes",
  cotizaciones: "Cotizaciones",
  contratos: "Contratos",
  pagos: "Pagos",
  agenda: "Agenda",
  eventos: "Eventos",
  comercial: "Comercial",
  produccion: "Producción",
  cocina: "Cocina",
  cocina_chef: "Cocina / Chef",
  compras: "Compras",
  montaje: "Montaje",
  personal: "Personal",
  proveedores: "Proveedores",
  pendientes: "Pendientes",
  ordenes_trabajo: "Órdenes de trabajo",
  orden_maestra: "Orden Maestra",
  itinerario_od: "Itinerario / Orden del día",
  recetas: "Recetas",
  inventario: "Inventario",
  documentos: "Documentos",
  finanzas: "Finanzas",
  modo_evento: "Día del evento",
  servicios_menu: "Servicios y menú",
  catalogo: "Catálogo",
  personal_prov: "Personal y proveedores",
};

const ModulosVisiblesPanel = () => {
  const { user, setUser } = useAuth();
  const permitidos = Array.isArray(user?.plan_modulos) ? user.plan_modulos : [];
  const [visibles, setVisibles] = useState(() => {
    const mv = user?.modulos_visibles;
    if (Array.isArray(mv) && mv.length > 0) return new Set(mv);
    return new Set(permitidos); // default: todo lo permitido visible
  });
  const [saving, setSaving] = useState(false);
  const [mostrarTodo, setMostrarTodo] = useState(!(Array.isArray(user?.modulos_visibles) && user.modulos_visibles.length > 0));

  const toggle = (m) => {
    setVisibles(prev => {
      const n = new Set(prev);
      n.has(m) ? n.delete(m) : n.add(m);
      return n;
    });
  };
  const activarTodos = () => setVisibles(new Set(permitidos));
  const desactivarTodos = () => setVisibles(new Set());

  const guardar = async () => {
    setSaving(true);
    try {
      const payload = mostrarTodo ? null : Array.from(visibles);
      const { data } = await axios.put(`${API}/mi-empresa/modulos-visibles`, { modulos_visibles: payload });
      // Refresca la sesión para que el sidebar/expediente aplique al instante
      try {
        const me = await axios.get(`${API}/auth/me`);
        if (me?.data?.user) setUser(me.data.user);
      } catch { /* silencioso */ }
      toast.success("Preferencia de módulos guardada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  if (!permitidos.length) {
    return (
      <Card className="border-stone-200"><CardContent className="p-6 text-sm text-stone-600">Tu plan aún no incluye módulos configurables.</CardContent></Card>
    );
  }

  return (
    <Card className="border-stone-200" data-testid="panel-modulos-visibles">
      <CardHeader>
        <CardTitle className="font-display text-xl flex items-center gap-2">
          <Settings className="w-5 h-5 text-emerald-900" /> Módulos de mi espacio de trabajo
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-stone-600">Decide qué módulos permitidos por tu plan aparecen en tu interfaz. Desactivar solo los oculta — los datos, permisos e históricos se conservan intactos.</p>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={mostrarTodo} onChange={e => setMostrarTodo(e.target.checked)} data-testid="chk-mostrar-todo" />
          <span>Mostrar todos los módulos permitidos por mi plan (default)</span>
        </label>

        {!mostrarTodo && (
          <>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={activarTodos} data-testid="btn-activar-todos">Activar todos</Button>
              <Button size="sm" variant="outline" onClick={desactivarTodos} data-testid="btn-desactivar-todos">Desactivar todos</Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {permitidos.map(m => (
                <label key={m} className="flex items-center gap-2 p-3 border rounded cursor-pointer hover:bg-stone-50" style={{ borderColor: "#e7e5e4" }} data-testid={`sw-modulo-${m}`}>
                  <input type="checkbox" checked={visibles.has(m)} onChange={() => toggle(m)} />
                  <span className="text-sm">{MODULOS_LABELS[m] || m}</span>
                </label>
              ))}
            </div>
          </>
        )}

        <div className="pt-2 flex justify-end">
          <Button onClick={guardar} disabled={saving} className="btn-primary" data-testid="btn-guardar-modulos">
            <Save className="w-4 h-4 mr-2" /> {saving ? "Guardando…" : "Guardar preferencia"}
          </Button>
        </div>
        <p className="text-xs text-stone-500">Nota: &ldquo;Resumen&rdquo; siempre permanece visible. Los módulos que tu plan no incluye no aparecen aquí (contáctanos si necesitas ampliarlos).</p>
      </CardContent>
    </Card>
  );
};
